import type { Metadata } from "next";
import { SITE_URL } from "@/lib/schema";
import type { Locale } from "./locales";
import { locales, defaultLocale, HINDI_SLUGS, localeSlugAliases } from "./locales";
import en from "./messages/en";
import hi from "./messages/hi";

const messages: Record<Locale, Record<string, import("./messages/en").PageMeta>> = { en, hi };

/** Detect locale from a URL pathname. */
export function detectLocale(pathname: string): Locale {
  if (pathname.startsWith("/hi/") || pathname === "/hi") return "hi";
  return "en";
}

/** Get the canonical path for a slug + locale. */
export function getLocalePath(slug: string, locale: Locale): string {
  const localizedSlug = localeSlugAliases[locale]?.[slug] ?? slug;
  const base = locale === "hi" ? `/hi/${localizedSlug}` : `/${localizedSlug}`;
  return base.endsWith("/") ? base : `${base}/`;
}

function withTrailingSlash(path: string): string {
  return path.endsWith("/") ? path : `${path}/`;
}

// Resolve a slug to its canonical path for `locale`, applying the per-locale
// alias map. Extracted so generateAlternates stays under the complexity-10
// ratchet; output is byte-identical to the prior inline logic (pinned by
// tests/seo-metadata.test.ts).
function canonicalPathFor(slug: string, locale: Locale): string {
  const resolved = (locale === "hi" ? localeSlugAliases.hi : localeSlugAliases.en)?.[slug] ?? slug;
  const base = locale === "hi" ? `/hi/${resolved}` : `/${resolved}`;
  return withTrailingSlash(base);
}

/**
 * Generate Next.js alternates (hreflang) for a page.
 * Pass `hasHindi: false` for pages without a Hindi version.
 *
 * `canonicalOverrideSlug` consolidates a DUPLICATE page onto a stronger one:
 * the page's canonical points at the winner's path AND the page exits the
 * hreflang cluster entirely (returns `{ canonical }` only, no `languages`).
 * A same-language duplicate must NOT emit hreflang into the winner's cluster —
 * that creates unconfirmed/half-paired annotations the winner never returns
 * (rule 27). Use this instead of a bare `alternates` override, which silently
 * strips `languages` via the `...overrides` spread in generatePageMetadata.
 */
export function generateAlternates(
  slug: string,
  locale: Locale,
  hasHindi = true,
  canonicalOverrideSlug?: string
): Metadata["alternates"] {
  if (canonicalOverrideSlug) {
    return { canonical: `${SITE_URL}${canonicalPathFor(canonicalOverrideSlug, locale)}` };
  }
  const enSlug = localeSlugAliases.en?.[slug] ?? slug;
  const hiSlug = localeSlugAliases.hi?.[slug] ?? slug;
  const canonicalPath = canonicalPathFor(slug, locale);

  // Emit absolute URLs. metadataBase normally absolutises alternates, but the
  // OpenNext Cloudflare renderer (next 16.2.6 + @opennextjs/cloudflare 1.19)
  // serves the relative form to the browser, costing 0.10 on Lighthouse SEO
  // (invalid canonical + invalid hreflang on /free-kundali/, /compatibility/,
  // /panchang/, /ai-astrologer/). Build the absolute URL here so there is no
  // resolution gap between Next render and edge serve.
  const canonical = `${SITE_URL}${canonicalPath}`;

  const languages: Record<string, string> = {
    en: `${SITE_URL}/${enSlug}/`,
    "x-default": `${SITE_URL}/${enSlug}/`,
  };
  if (hasHindi) {
    languages.hi = `${SITE_URL}/hi/${hiSlug}/`;
  }
  return { canonical, languages };
}

/**
 * Generate a complete Next.js Metadata object for a page.
 *
 * Usage:
 *   export const metadata = generatePageMetadata("panchang", "en");
 *   export const metadata = generatePageMetadata("panchang", "hi");
 *
 * The locale is usually known at compile time from the file's location:
 *   - /app/panchang/page.tsx      → locale "en"
 *   - /app/hi/panchang/page.tsx   → locale "hi"
 */
/**
 * Slugs whose <title> + description should be prefixed with today's date.
 * Daily production deploy (.github/workflows/deploy-web-cloudflare.yml) refreshes
 * these on each deploy, signaling content freshness to Google for "today's X"
 * queries. Build-time injection — no runtime work.
 */
const TIME_SENSITIVE_SLUGS = new Set([
  "panchang",
  "tithi-today",
  "choghadiya",
  "rashifal",
  "rahu-kaal",
  "shubh-muhurat",
  "monthly-rashifal",
  "weekly-rashifal",
]);

// Computed once at module load. The daily Cloudflare deploy workflow
// guarantees these refresh on Jan 1.
const CURRENT_YEAR = new Date().getFullYear();
const NEXT_YEAR = CURRENT_YEAR + 1;
const PREV_YEAR = CURRENT_YEAR - 1;
const YEAR_2D = String(CURRENT_YEAR).slice(-2);

function applyYearTokens(s: string): string {
  return s
    .replace(/\{year\}/g, String(CURRENT_YEAR))
    .replace(/\{nextyear\}/g, String(NEXT_YEAR))
    .replace(/\{prevyear\}/g, String(PREV_YEAR))
    .replace(/\{year_2d\}/g, YEAR_2D);
}

const HI_WEEKDAYS = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
const HI_MONTHS = [
  "जनवरी",
  "फरवरी",
  "मार्च",
  "अप्रैल",
  "मई",
  "जून",
  "जुलाई",
  "अगस्त",
  "सितंबर",
  "अक्टूबर",
  "नवंबर",
  "दिसंबर",
];

// Exported so per-route generateMetadata() on city timing pages
// (panchang/choghadiya/rahu-kaal [city]) reuse the SAME IST date string the
// TIME_SENSITIVE_SLUGS root pages prefix — one source of truth, no drift
// (rule 23/24). Caller must keep the route's `revalidate` <= 3600 so the
// title date never lags past an hour.
export function formatDateForTitle(locale: Locale, d: Date = new Date()): string {
  const istParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(d);
  const values = Object.fromEntries(istParts.map((part) => [part.type, part.value]));
  const istDate = new Date(Number(values.year), Number(values.month) - 1, Number(values.day));

  if (locale === "hi") {
    return `${HI_WEEKDAYS[istDate.getDay()]}, ${istDate.getDate()} ${
      HI_MONTHS[istDate.getMonth()]
    } ${istDate.getFullYear()}`;
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function generatePageMetadata(
  slug: string,
  locale: Locale,
  overrides?: Partial<Metadata>,
  canonicalOverrideSlug?: string
): Metadata {
  const msg = messages[locale]?.[slug];
  if (!msg) {
    console.warn(`[i18n] No message for slug "${slug}" in locale "${locale}"`);
  }

  const metadataText = localizedMetadataText(slug, locale, msg);
  const keywords = msg?.keywords?.map(applyYearTokens);
  const hasHindi = HINDI_SLUGS.has(slug);

  return {
    title: metadataText.title,
    description: metadataText.description,
    ...(keywords ? { keywords } : {}),
    openGraph: {
      title: metadataText.ogTitle,
      description: metadataText.ogDescription,
      siteName: "Ishvaram",
      type: "website",
    },
    alternates: generateAlternates(slug, locale, hasHindi, canonicalOverrideSlug),
    ...overrides,
  };
}

function localizedMetadataText(
  slug: string,
  locale: Locale,
  msg: (typeof messages)[Locale][string] | undefined
) {
  const title = msg?.title ?? `Ishvaram — ${slug}`;
  const description = msg?.description ?? "";
  const ogTitle = msg?.ogTitle ?? title;
  const ogDescription = msg?.ogDescription ?? description;
  const text = { title, description, ogTitle, ogDescription };
  const datedText = TIME_SENSITIVE_SLUGS.has(slug) ? withDatePrefix(text, locale) : text;
  return applyYearToMetadataText(datedText);
}

function withDatePrefix(
  text: { title: string; description: string; ogTitle: string; ogDescription: string },
  locale: Locale
) {
  const dateStr = formatDateForTitle(locale);
  return {
    title: `${dateStr} — ${text.title}`,
    description: `${dateStr}. ${text.description}`,
    ogTitle: `${dateStr} — ${text.ogTitle}`,
    ogDescription: `${dateStr}. ${text.ogDescription}`,
  };
}

function applyYearToMetadataText(text: {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
}) {
  return {
    title: applyYearTokens(text.title),
    description: applyYearTokens(text.description),
    ogTitle: applyYearTokens(text.ogTitle),
    ogDescription: applyYearTokens(text.ogDescription),
  };
}

/**
 * Check which locale slugs are missing translations.
 * Useful for auditing coverage before adding a new language.
 */
export function auditTranslations(): {
  locale: Locale;
  slug: string;
  missing: boolean;
}[] {
  const allSlugs = new Set([...Object.keys(en), ...Object.keys(hi)]);
  const results: { locale: Locale; slug: string; missing: boolean }[] = [];
  for (const locale of locales) {
    for (const slug of allSlugs) {
      results.push({
        locale,
        slug,
        missing: !messages[locale]?.[slug],
      });
    }
  }
  return results.filter((r) => r.missing);
}

export { locales, defaultLocale };
export type { Locale };
